import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, HelpCircle, Info, Monitor, ShoppingBag, MessageSquare, HeadphonesIcon, Shield } from 'lucide-react';
import { getFAQs } from '../utils/api';

const FAQs = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [faqData, setFaqData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default static FAQ data (fallback)
  const defaultFaqCategories = [
    {
      category: 'General',
      icon: Info,
      questions: [
        {
          question: 'What is LexDoRight?',
          answer: 'LexDoRight is a platform that sits at the intersection of law, governance, and technology. We provide tools, resources, and community support to help legal professionals, policymakers, entrepreneurs, government officials, and students navigate complex legal and governance frameworks with clarity and confidence.'
        },
        {
          question: 'Who can use LexDoRight?',
          answer: 'LexDoRight is designed for law professionals, entrepreneurs and business owners, government officials and public servants, researchers and students, board secretaries, and anyone dealing with legal, policy, or governance decisions in their daily work.'
        },
        {
          question: 'How much does LexDoRight cost?',
          answer: 'LexDoRight offers various pricing options depending on the products and applications you choose. Some resources are free, while premium applications and eBooks require a subscription or one-time purchase. Please check individual product pages for specific pricing.'
        },
        {
          question: 'Do I need to create an account?',
          answer: 'You can browse our resources, blog, and applications without an account. However, to purchase products, participate in the forum, or access certain premium features, you will need to register for a free account.'
        }
      ]
    },
    {
      category: 'Applications',
      icon: Monitor,
      questions: [
        {
          question: 'What is LexProcure PH?',
          answer: 'LexProcure PH is a comprehensive procurement tool designed for Philippine government and private sector procurement professionals. It helps navigate the complexities of RA 12009 (the new Government Procurement Reform Act) and provides structured guidance for compliant procurement processes.'
        },
        {
          question: 'What is LexDelta?',
          answer: 'LexDelta is a proofreading and policy drafting tool designed for policymakers, legislators, and legal professionals. It helps ensure accuracy, consistency, and compliance in policy documents, contracts, and legislative drafts.'
        },
        {
          question: 'What is LexAgenda?',
          answer: 'LexAgenda is the board secretary\'s survival kit, providing meeting preparation checklists, agenda templates, minutes scaffolds, action-item trackers, and governance calendars to help keep board meetings organized and compliant.'
        },
        {
          question: 'How do I access the applications?',
          answer: 'LexProcure PH can be accessed at lexprocure-ph.lexdoright.com. LexDelta and LexAgenda are currently under development and will be available soon. Subscribe to our newsletter or check the Applications page for updates.'
        }
      ]
    },
    {
      category: 'Products & Resources',
      icon: ShoppingBag,
      questions: [
        {
          question: 'What types of eBooks do you offer?',
          answer: 'We offer comprehensive guides, templates, and reference materials covering legal, policy, and governance topics. Our eBooks include procurement guides, corporate governance handbooks, policy drafting manuals, and compliance checklists tailored for professionals in various sectors.'
        },
        {
          question: 'What are LexDoRight souvenirs?',
          answer: 'Our professional souvenirs include governance-themed desk accessories, corporate gifts, event kits, printed references, and elegant office essentials designed to keep legal and governance concepts visible and accessible in your workspace.'
        },
        {
          question: 'How do I purchase products?',
          answer: 'Browse our Products section, add items to your cart, and proceed to checkout. We accept major credit cards and other secure payment methods through Stripe. You will receive a confirmation email after your purchase.'
        },
        {
          question: 'Can I get a refund?',
          answer: 'Digital products (eBooks) are generally non-refundable once downloaded. Physical products (souvenirs) may be returned within 14 days if unused and in original packaging. Please contact admin@lexdoright.com for refund requests.'
        }
      ]
    },
    {
      category: 'Forum',
      icon: MessageSquare,
      questions: [
        {
          question: 'Do I need an account to use the forum?',
          answer: 'You can browse forum topics without an account, but you must be signed in to post new topics or reply to existing discussions.'
        },
        {
          question: 'Is the advice in the forum legally binding?',
          answer: 'No. All information in the LexDoRight forum is for general information and educational purposes only and does not constitute legal advice. For formal legal advice, consult a licensed lawyer or the appropriate government agency.'
        },
        {
          question: 'How do I suggest a new application or product?',
          answer: 'Visit the Forum, select the "Suggested New Apps/Products" category, and create a new topic describing your suggestion. Our team reviews all suggestions and may reach out for more details.'
        },
        {
          question: 'What are the forum rules?',
          answer: 'Be respectful, post in the appropriate category, avoid spam and offensive content, do not share personal case information, and follow all guidelines posted on the forum page. Violations may result in post deletion or account suspension.'
        }
      ]
    },
    {
      category: 'Technical Support',
      icon: HeadphonesIcon,
      questions: [
        {
          question: 'I forgot my password. How do I reset it?',
          answer: 'Currently, password reset functionality is being developed. Please contact support@lexdoright.com with your registered email, and our team will assist you in recovering your account.'
        },
        {
          question: 'The website is not loading properly. What should I do?',
          answer: 'Try clearing your browser cache and cookies, or try a different browser. If the problem persists, contact support@lexdoright.com with details about your browser and the issue you\'re experiencing.'
        },
        {
          question: 'How do I update my account information?',
          answer: 'Log in to your account and navigate to your profile page (accessible from the navbar when logged in). You can update your name, email, and other account details there.'
        },
        {
          question: 'Who do I contact for technical issues?',
          answer: 'For technical support, email support@lexdoright.com. For general inquiries, questions, or feedback, email admin@lexdoright.com. We typically respond within 24-48 hours during business days.'
        }
      ]
    },
    {
      category: 'Account & Privacy',
      icon: Shield,
      questions: [
        {
          question: 'How is my personal information protected?',
          answer: 'We take data security seriously and use industry-standard encryption and security measures to protect your personal information. We do not sell or share your personal data with third parties without your consent.'
        },
        {
          question: 'Can I delete my account?',
          answer: 'Yes, you can request account deletion by contacting admin@lexdoright.com. Please note that some information may be retained for legal or administrative purposes as outlined in our Terms of Service.'
        },
        {
          question: 'What information do you collect?',
          answer: 'We collect information you provide during registration (name, email, password), purchase information for order processing, and usage data to improve our services. You can review our Privacy Policy for detailed information.'
        }
      ]
    }
  ];

  const categoryIcons = {
    'General': Info,
    'Applications': Monitor,
    'Products & Resources': ShoppingBag,
    'Forum': MessageSquare,
    'Technical Support': HeadphonesIcon,
    'Account & Privacy': Shield
  };

  useEffect(() => {
    const loadFAQs = async () => {
      try {
        const res = await getFAQs();
        const cmsFaqs = res.data || [];
        
        if (cmsFaqs.length > 0) {
          // Group CMS FAQs by category
          const cmsGroupedFaqs = {};
          cmsFaqs.forEach(faq => {
            if (!cmsGroupedFaqs[faq.category]) {
              cmsGroupedFaqs[faq.category] = [];
            }
            cmsGroupedFaqs[faq.category].push({
              question: faq.question,
              answer: faq.answer
            });
          });
          
          // Merge with default: For each default category, use CMS if exists, else use default
          const mergedCategories = defaultFaqCategories.map(defaultCat => {
            if (cmsGroupedFaqs[defaultCat.category] && cmsGroupedFaqs[defaultCat.category].length > 0) {
              // Replace default category with CMS content
              return {
                ...defaultCat,
                questions: cmsGroupedFaqs[defaultCat.category]
              };
            }
            // Keep default content for this category
            return defaultCat;
          });
          
          // Add any CMS categories that don't exist in defaults
          Object.keys(cmsGroupedFaqs).forEach(category => {
            const existsInDefaults = defaultFaqCategories.some(d => d.category === category);
            if (!existsInDefaults) {
              mergedCategories.push({
                category,
                icon: categoryIcons[category] || HelpCircle,
                questions: cmsGroupedFaqs[category]
              });
            }
          });
          
          setFaqData(mergedCategories);
        } else {
          // Use default static content
          setFaqData(defaultFaqCategories);
        }
      } catch (error) {
        console.error('Failed to load FAQs:', error);
        setFaqData(defaultFaqCategories);
      } finally {
        setLoading(false);
      }
    };
    
    loadFAQs();
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Helper function to render text with styled "Lex" brand
  const renderStyledText = (text) => {
    const parts = text.split(/(Lex(?:DoRight|Procure|Delta|Agenda))/g);
    return parts.map((part, index) => {
      if (part.startsWith('Lex')) {
        const rest = part.slice(3);
        return (
          <span key={index}>
            <span className="text-[#8B1818] font-semibold">Lex</span>{rest}
          </span>
        );
      }
      return part;
    });
  };

  const faqCategories = faqData || defaultFaqCategories;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B1818]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Header */}
      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Find answers to common questions about <span className="text-[#8B1818] font-semibold">Lex</span>DoRight, our applications, products, and services
          </p>
        </div>
      </div>

      {/* FAQs Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {faqCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-12">
            <div className="flex items-center mb-6">
              <category.icon className="h-7 w-7 text-[#8B1818] mr-3" />
              <h2 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Playfair Display, serif'}}>
                {category.category}
              </h2>
            </div>
            <div className="space-y-4">
              {category.questions.map((faq, index) => {
                const globalIndex = `${categoryIndex}-${index}`;
                const isOpen = openIndex === globalIndex;
                
                return (
                  <div
                    key={globalIndex}
                    className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFAQ(globalIndex)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="text-lg font-semibold text-slate-900 pr-4">
                        {renderStyledText(faq.question)}
                      </span>
                      {isOpen ? (
                        <ChevronUp className="h-5 w-5 text-[#8B1818] flex-shrink-0" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
                      )}
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-4 pt-2">
                        <p className="text-slate-700 leading-relaxed">
                          {renderStyledText(faq.answer)}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Contact Section */}
        <div className="mt-16 bg-gradient-to-br from-[#8B1818] to-[#701313] rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
            Still have questions?
          </h3>
          <p className="text-white/90 mb-6">
            Can't find the answer you're looking for? Our team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:admin@lexdoright.com"
              className="bg-white text-[#8B1818] px-6 py-3 rounded-lg font-semibold hover:bg-white/90 transition-colors"
            >
              Contact Support
            </a>
            <a
              href="/forum"
              className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors"
            >
              Visit Forum
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQs;
